export * from './direct-call-voice-activity';
// US 35617: the hands-free turn controller, split out under the file-size gate.
export * from './direct-call-hands-free';

export interface LocalSpeechRecording {
  // External interruption only, not normal completion or user cancellation.
  readonly interrupted?: AbortSignal;
  // Why `interrupted` fired; 'microphone-busy' when another call took the mic.
  interruptionCause?(): 'microphone-busy' | null;
  // Normalized microphone power (0..1); unavailable adapters omit this.
  inputLevel?(): number;
  // Stop recording, transcribe locally, and release capture resources.
  finish(): Promise<string>;
  // Release capture resources; safe to call after finish or cancellation.
  cancel(): void;
  // US 35617 AC2: hold capture without releasing the microphone. Nothing is
  // kept while paused; resume continues the same recording. An adapter that
  // cannot do both omits both, and mute then releases capture as before.
  pause?(): void;
  resume?(): void;
}

export type LocalSpeechDiagnosticObserver = (
  stage: 'permission' | 'capture-stop',
  outcome: 'started' | 'succeeded' | 'failed' | 'cancelled',
  code: 'none' | 'permission-denied' | 'cancelled' | 'unknown-failure',
  durationMs: number
) => void;

export interface LocalSpeechInputAdapter {
  // Request permission and acquire the microphone. No cloud fallback.
  // Abort must release capture and settle any pending native operation.
  // Reject with an error whose `code` is 'microphone-busy' when another call holds the mic.
  readonly reportsCaptureDiagnostics?: boolean;
  // US 35617 AC2: its recordings pause and resume (false mute). Absent means
  // mute releases capture, and a muted call opens nothing.
  readonly canPause?: boolean;
  start(
    signal: AbortSignal,
    diagnostic?: LocalSpeechDiagnosticObserver
  ): Promise<LocalSpeechRecording>;
}

export interface LocalSpeechTranscriber {
  // On-device inference only; run off the UI thread and honor cancellation.
  transcribeFile(path: string, signal: AbortSignal): Promise<string>;
}

export type DirectCallInputFailure =
  'capture' | 'microphone-busy' | 'transcription';

// Duck-typed: adapters live in other bundles and cannot share an error class.
const isMicrophoneBusy = (error: unknown): boolean =>
  (error as { code?: unknown } | null)?.code === 'microphone-busy';

export type DirectCallInputState =
  'idle' | 'starting' | 'recording' | 'transcribing' | 'cancelling' | 'closed';

// Produces an editable draft only. Sending is a separate explicit user action.
export class DirectCallInput {
  private currentState: DirectCallInputState = 'idle';
  get state(): DirectCallInputState {
    return this.currentState;
  }
  private set state(value: DirectCallInputState) {
    this.currentState = value;
    this.onStateChange();
  }
  draft = '';
  get inputLevel(): number {
    if (this.state !== 'recording' || this.paused) return 0;
    const level = this.recording?.inputLevel?.() ?? 0;
    return Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
  }
  failure: DirectCallInputFailure | null = null;
  private abort: AbortController | null = null;
  private recording: LocalSpeechRecording | null = null;
  private revision = 0;
  private pending = false;
  private closed = false;
  private removeInterruption: (() => void) | null = null;
  private paused = false;

  /** US 35617 AC2: the adapter's recordings can be held by a false mute. */
  get canHold(): boolean {
    return !!this.adapter.canPause;
  }

  /** US 35617 AC2: the open recording is paused by a false mute. */
  get held(): boolean {
    return this.paused && this.state === 'recording';
  }

  constructor(
    private readonly adapter: LocalSpeechInputAdapter,
    private readonly onStateChange: () => void = () => {}
  ) {}

  setDraft(text: string): void {
    this.revision++;
    this.draft = text;
  }

  async start(): Promise<boolean> {
    if (this.closed || this.state !== 'idle') return false;
    const abort = new AbortController();
    this.abort = abort;
    this.failure = null;
    this.state = 'starting';
    this.pending = true;
    try {
      const recording = await this.adapter.start(abort.signal);
      if (abort.signal.aborted) {
        recording.cancel();
        return false;
      }
      this.recording = recording;
      const interrupted = () => {
        // US 35617 AC2: a recording held by a false mute is not capturing,
        // so losing it is not a failure to show; the hold reopens or stops.
        if (!this.paused)
          this.failure =
            recording.interruptionCause?.() === 'microphone-busy'
              ? 'microphone-busy'
              : 'capture';
        this.cancel();
      };
      if (recording.interrupted?.aborted) {
        interrupted();
        return false;
      }
      recording.interrupted?.addEventListener('abort', interrupted, {
        once: true,
      });
      this.removeInterruption = () =>
        recording.interrupted?.removeEventListener('abort', interrupted);
      this.state = 'recording';
      return true;
    } catch (error) {
      if (!abort.signal.aborted)
        this.failure = isMicrophoneBusy(error) ? 'microphone-busy' : 'capture';
      return false;
    } finally {
      this.pending = false;
      if (!this.recording) this.reset();
    }
  }

  /**
   * US 35617 AC2: pause (true) or resume (false) the open recording without
   * releasing the microphone. False, with nothing changed, when no recording
   * is open, the adapter cannot pause, or the native call throws.
   */
  hold(paused: boolean): boolean {
    const recording = this.recording;
    if (this.state !== 'recording' || !recording?.pause || !recording.resume)
      return false;
    try {
      if (paused) recording.pause();
      else recording.resume();
    } catch {
      return false;
    }
    this.paused = paused;
    this.onStateChange();
    return true;
  }

  async finish(): Promise<boolean> {
    if (this.state !== 'recording' || !this.recording) return false;
    const recording = this.recording;
    const abort = this.abort!;
    const revision = this.revision;
    this.state = 'transcribing';
    this.pending = true;
    try {
      const text = await recording.finish();
      if (abort.signal.aborted || revision !== this.revision) return false;
      this.setDraft(text);
      return true;
    } catch {
      if (!abort.signal.aborted) this.failure = 'transcription';
      return false;
    } finally {
      try {
        this.removeInterruption?.();
        this.removeInterruption = null;
        recording.cancel();
      } finally {
        this.pending = false;
        this.recording = null;
        this.reset();
      }
    }
  }

  cancel(): void {
    this.paused = false;
    this.removeInterruption?.();
    this.removeInterruption = null;
    this.abort?.abort();
    try {
      this.recording?.cancel();
    } finally {
      this.recording = null;
      this.state = this.closed
        ? 'closed'
        : this.pending
          ? 'cancelling'
          : 'idle';
    }
  }

  end(): void {
    this.closed = true;
    this.setDraft('');
    this.cancel();
  }

  private reset(): void {
    this.paused = false;
    this.abort = null;
    this.state = this.closed ? 'closed' : 'idle';
  }
}
